// Fictional, internally consistent example data. This player never calls app services.
export const DEMO_MEAL = {
  title: "Salmon poke bowl",
  photo: "/images/pokebowl.png",
  time: "12:30",
  calories: 620,
  protein: 30,
  fiber: 9,
  ingredients: [
    ["Salmon", "120 g"],
    ["Brown rice", "150 g"],
    ["Avocado", "70 g"],
    ["Edamame and cucumber", "100 g"],
  ],
} as const;

export const DEMO_PROTEIN = { beforeLunch: 30, lunch: DEMO_MEAL.protein, chicken: 45, target: 100 };
export const DEMO_DURATION = 53000;
export const DEMO_CHAPTERS = [
  { label: "Capture", time: 6500 },
  { label: "Understand", time: 19500 },
  { label: "Act", time: 31500 },
] as const;

export type DemoScreen = "voice-log" | "voice-entry" | "voice-edit" | "overview" | "menu" | "camera" | "analysis" | "review" | "log" | "detail" | "recommendation" | "prefill" | "nutrient-status" | "fridge-analysis" | "fridge-result" | "menu-analysis" | "menu-result";
export type DemoTarget = "voice-change" | "voice-mic" | "plus" | "camera" | "shutter" | "save" | "meal" | "back" | "home" | "recommendation" | "add" | "send" | "filter-low" | "filter-good" | "filter-high" | "menu-choice";
export type DemoScene = {
  id: string;
  start: number;
  end: number;
  screen: DemoScreen;
  caption: string;
  chapter: number;
  target?: DemoTarget;
  chicken?: boolean;
  fridge?: boolean;
  restaurant?: boolean;
  nutrientStatus?: "low" | "good" | "high";
  complete?: boolean;
};

// Keep reveal and tap motion at their original pace; shorten the quiet reading holds.
export const DEMO_SCENES: readonly DemoScene[] = [
  { id: "start", start: 0, end: 3500, screen: "overview", caption: "Start with what you eat.", chapter: 0, target: "plus" },
  { id: "menu", start: 3500, end: 6500, screen: "menu", caption: "A photo is all it takes.", chapter: 0, target: "camera" },
  { id: "camera", start: 6500, end: 9000, screen: "camera", caption: "Snap your meal.", chapter: 0, target: "shutter" },
  { id: "analysis", start: 9000, end: 11000, screen: "analysis", caption: "Let Nomsy take a closer look.", chapter: 0 },
  { id: "review", start: 11000, end: 16000, screen: "review", caption: "Your meal, understood.", chapter: 0, target: "save" },
  { id: "logged", start: 16000, end: 19500, screen: "log", caption: "A little more of your story, saved.", chapter: 1, target: "meal" },
  { id: "detail", start: 19500, end: 24000, screen: "detail", caption: "Every ingredient, remembered.", chapter: 1, target: "back" },
  { id: "back", start: 24000, end: 27000, screen: "log", caption: "Your food history stays with you.", chapter: 1, target: "home" },
  { id: "nutrients", start: 27000, end: 31500, screen: "overview", caption: "Know what you need next.", chapter: 1, target: "recommendation" },
  { id: "suggestion", start: 31500, end: 36000, screen: "recommendation", caption: "One clear next step. Just for you.", chapter: 2, target: "add" },
  { id: "prefill", start: 36000, end: 40000, screen: "prefill", caption: "After eating, log it in a few taps.", chapter: 2, target: "send", chicken: true },
  { id: "chicken-analysis", start: 40000, end: 42000, screen: "analysis", caption: "The details are already there.", chapter: 2, chicken: true },
  { id: "chicken-review", start: 42000, end: 46000, screen: "review", caption: "Review it. Make it part of your day.", chapter: 2, target: "save", chicken: true },
  { id: "chicken-logged", start: 46000, end: 49500, screen: "log", caption: "Another good choice, remembered.", chapter: 2, target: "home", chicken: true },
  { id: "complete", start: 49500, end: DEMO_DURATION, screen: "overview", caption: "Small choices. A clearer picture.", chapter: 2, complete: true },
];

export function getDemoScene(time: number): DemoScene {
  return DEMO_SCENES.find((scene) => time >= scene.start && time < scene.end) ?? DEMO_SCENES[0];
}

export function progress(time: number, start: number, duration: number) {
  return Math.max(0, Math.min(1, (time - start) / duration));
}

export function easeOut(value: number) {
  return 1 - (1 - value) ** 3;
}
